import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { articles, type ArticleData, type ArticleSection } from '@/mocks/articles';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  const article = useMemo(() => {
    return articles.find((a) => a.slug === slug) || null;
  }, [slug]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center px-4">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-accent-100 text-accent-600">
              <i className="ri-file-unknow-line text-2xl"></i>
            </div>
            <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-2">Artykuł nie znaleziony</h1>
            <p className="text-sm text-foreground-500 mb-6">Przepraszamy, szukany artykuł nie istnieje lub został usunięty.</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-2.5 bg-accent-500 text-background-50 rounded-full text-sm font-semibold hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
            >
              <i className="ri-home-line"></i>
              Wróć na stronę główną
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://kakobuypolska.beer';

  const jsonLdArticle = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.h1Title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.date,
    author: { '@type': 'Organization', name: 'KakobuyPolska' },
    publisher: { '@type': 'Organization', name: 'KakobuyPolska', url: siteUrl },
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${siteUrl}/artykul/${article.slug}` },
  };

  const faqSection = article.sections.find((s) => s.type === 'faq');
  const jsonLdFaq = faqSection && faqSection.faqs ? {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqSection.faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  } : null;

  const jsonLdBreadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Strona główna', item: `${siteUrl}/` },
      { '@type': 'ListItem', position: 2, name: article.h1Title, item: `${siteUrl}/artykul/${article.slug}` },
    ],
  };

  const renderSection = (section: ArticleSection, idx: number) => {
    switch (section.type) {
      case 'heading':
        return (
          <h2 key={idx} className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
            {section.headingText}
          </h2>
        );
      case 'paragraph':
        return (
          <p key={idx} className="text-base text-foreground-700 leading-relaxed mb-4">
            {section.content}
          </p>
        );
      case 'table':
        return (
          <div key={idx} className="overflow-x-auto rounded-xl border border-background-200/70 mb-6">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-background-100">
                  {section.headers?.map((h, i) => (
                    <th key={i} className="text-left px-5 py-3.5 font-semibold text-foreground-800 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-background-200/60">
                {section.rows?.map((row, ri) => (
                  <tr key={ri} className="hover:bg-background-100/50 transition-colors">
                    {row.cells.map((cell, ci) => (
                      <td key={ci} className="px-5 py-3.5 text-foreground-600 whitespace-nowrap">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'steps':
        return (
          <div key={idx} className="space-y-5 mb-6">
            {section.steps?.map((step) => (
              <div key={step.number} className="flex gap-4">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-500 text-background-50 font-bold text-sm flex-shrink-0">
                  {step.number}
                </div>
                <div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-1">{step.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        );
      case 'faq':
        return (
          <section key={idx} className="bg-background-100 rounded-2xl p-6 md:p-10 border border-background-200/70">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-8 text-center">
              {section.headingText}
            </h2>
            <div className="space-y-6">
              {section.faqs?.map((faq, fi) => (
                <div key={fi}>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2">{faq.question}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>
        );
      case 'categories':
        return (
          <div key={idx} className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
            {section.categoryItems?.map((item) => (
              <div key={item.name} className="bg-background-100 rounded-xl p-4 border border-background-200/60">
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">{item.name}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        );
      case 'cta':
        return (
          <section key={idx} className="text-center py-8">
            <div className="bg-background-100 rounded-2xl p-8 md:p-10 border border-background-200/70">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
                {section.headingText}
              </h2>
              <p className="text-sm md:text-base text-foreground-600 mb-8 max-w-lg mx-auto leading-relaxed">
                {section.content}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                {section.ctaExternal ? (
                  <a
                    href={section.ctaUrl}
                    target="_blank"
                    rel="noopener nofollow"
                    className="inline-flex items-center gap-2 px-8 py-3.5 bg-accent-500 text-background-50 rounded-full text-base font-semibold hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
                  >
                    <i className="ri-fire-line text-lg"></i>
                    {section.ctaText}
                  </a>
                ) : null}
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-8 py-3.5 border border-accent-500 text-accent-600 rounded-full text-base font-semibold hover:bg-accent-50 transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-home-line text-lg"></i>
                  Wróć na stronę główną
                </Link>
              </div>
            </div>
          </section>
        );
      case 'list':
        return (
          <ul key={idx} className="space-y-2 mb-6 list-none">
            {section.items?.map((item, li) => (
              <li key={li} className="flex items-start gap-2.5 text-foreground-700">
                <span className="w-5 h-5 flex items-center justify-center text-accent-500 flex-shrink-0 mt-0.5">
                  <i className="ri-check-line text-sm"></i>
                </span>
                <span className="text-sm leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <Navbar />
      <main className="pt-20">
        {/* Schema.org Structured Data */}
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdArticle) }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdBreadcrumb) }} />
        {jsonLdFaq && (
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLdFaq) }} />
        )}

        <article className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-accent-500 transition-colors whitespace-nowrap">Strona główna</Link>
            <i className="ri-arrow-right-s-line text-sm flex-shrink-0"></i>
            <span className="text-foreground-700 whitespace-nowrap">{article.category}</span>
            <i className="ri-arrow-right-s-line text-sm flex-shrink-0"></i>
            <span className="text-foreground-700 truncate">{article.h1Title}</span>
          </nav>

          {/* Article Header */}
          <header className="mb-10">
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-800 whitespace-nowrap">
                <i className="ri-guide-line"></i>
                {article.category}
              </span>
              <span className="text-xs text-foreground-500">{article.date}</span>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-5">
              {article.h1Title}
            </h1>
            <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
              {article.excerpt}
            </p>
          </header>

          {/* Featured Image */}
          <div className="w-full aspect-[16/7] rounded-2xl overflow-hidden mb-12 bg-background-100">
            <img
              src={article.imageUrl}
              alt={article.imageAlt}
              className="w-full h-full object-cover"
              title={article.h1Title}
            />
          </div>

          {/* Article Content */}
          <div className="space-y-1">
            {article.sections.map((section, idx) => (
              <div key={idx} className={section.type === 'heading' || section.type === 'faq' || section.type === 'cta' ? 'mt-10 first:mt-0' : ''}>
                {renderSection(section, idx)}
              </div>
            ))}
          </div>
        </article>

        {/* Internal Link Bar */}
        <div className="bg-background-100 border-t border-background-200/60 py-6">
          <div className="max-w-3xl mx-auto px-4 md:px-6 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sm text-foreground-600">
              <i className="ri-links-line mr-1.5"></i>
              Powiązane strony:
            </p>
            <div className="flex flex-wrap items-center gap-3">
              <Link to="/" className="text-sm text-accent-600 hover:text-accent-700 transition-colors whitespace-nowrap">
                <i className="ri-home-line mr-1"></i>Strona główna
              </Link>
              <Link to="/artykul/kakobuy-spreadsheet-polska-poradnik" className="text-sm text-foreground-600 hover:text-accent-500 transition-colors whitespace-nowrap">
                <i className="ri-book-open-line mr-1"></i>Poradnik główny
              </Link>
              <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-sm text-accent-600 hover:text-accent-700 transition-colors whitespace-nowrap">
                <i className="ri-external-link-line mr-1"></i>kaspreadsheet.com
              </a>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}